/**
 * The enabled locale set — the one list every switcher, catalogue and test reads.
 *
 * CONFIG, NOT CONSTANTS. Trim `LOCALES` to a single entry and the game is single-language:
 * `hasLocaleChoice` goes false and the picker stops rendering on its own, because a control
 * that offers one option is a lie. Nothing downstream hardcodes a locale.
 *
 * Two shapes for the same thing, and they are not interchangeable. `id` is the underscore form
 * (`en_GB`) — it is what the JSON filenames use, what `GET /api/i18n/:locale` expects, and what
 * `backend/src/core/i18n/i18n.constants.ts` enumerates. `tag` is the BCP-47 form (`en-GB`) that
 * `Intl` needs; never hand `Intl` a raw id, it throws on the underscore.
 *
 * The fallback is `en_GB`, not `en_US`, because core's own locale list says `en_GB` and a
 * mismatch here would mean the override request 404s for the one locale that must never miss.
 *
 * Detection is deliberately loose about region: a visitor arriving with `de-AT` gets German
 * rather than English, since the wrong dialect of a language you read beats a language you
 * do not. A stored pick always wins over the browser — it was chosen, not sniffed.
 */

export const LOCALES = [
  { id: 'en_GB', tag: 'en-GB', short: 'EN', label: 'English',       english: 'English'   },
  { id: 'de_DE', tag: 'de-DE', short: 'DE', label: 'Deutsch',       english: 'German'    },
  { id: 'pl_PL', tag: 'pl-PL', short: 'PL', label: 'Polski',        english: 'Polish'    },
  { id: 'nb_NO', tag: 'nb-NO', short: 'NO', label: 'Norsk bokmål',  english: 'Norwegian' },
  { id: 'sv_SE', tag: 'sv-SE', short: 'SV', label: 'Svenska',       english: 'Swedish'   },
  { id: 'hu_HU', tag: 'hu-HU', short: 'HU', label: 'Magyar',        english: 'Hungarian' },
] as const;

export type LocaleId = (typeof LOCALES)[number]['id'];

export interface LocaleMeta {
  id: LocaleId;
  tag: string;
  short: string;
  /** The language's own name for itself — what the picker shows. */
  label: string;
  english: string;
}

/** The tongue every other locale falls back to, and the only one bundled eagerly. */
export const DEFAULT_LOCALE: LocaleId = 'en_GB';

export const LOCALE_IDS = LOCALES.map((l) => l.id) as readonly LocaleId[];

/** False on a single-language build — the picker must not render at all. */
export const hasLocaleChoice = LOCALES.length > 1;

export function isLocaleId(v: unknown): v is LocaleId {
  return typeof v === 'string' && (LOCALE_IDS as readonly string[]).includes(v);
}

/** Metadata for one locale; unknown ids resolve to the fallback's row rather than throwing. */
export function localeMeta(id: string): LocaleMeta {
  return (LOCALES.find((l) => l.id === id) ?? LOCALES.find((l) => l.id === DEFAULT_LOCALE)!) as LocaleMeta;
}

/** BCP-47 tag for `Intl`. */
export function localeTag(id: string): string {
  return localeMeta(id).tag;
}

/** The language's endonym, for the switcher. */
export function localeLabel(id: string): string {
  return localeMeta(id).label;
}

/**
 * Resolve the locale to boot in: the remembered pick, then the browser's language list, then
 * the fallback. Region is matched first (`de-DE` beats `de-AT` when both are offered), then
 * language alone, so `de` and `de-CH` both land on `de_DE`.
 *
 * Pure — the caller supplies both inputs, which is what makes the ordering testable.
 */
export function detectLocale(
  navigatorLanguages: readonly string[] = [],
  stored?: string | null,
): LocaleId {
  if (isLocaleId(stored)) return stored;

  for (const raw of navigatorLanguages) {
    if (typeof raw !== 'string' || !raw) continue;
    const want = raw.toLowerCase().replace(/_/g, '-');
    const exact = LOCALES.find((l) => l.tag.toLowerCase() === want);
    if (exact) return exact.id;
    const lang = want.split('-')[0];
    const byLang = LOCALES.find((l) => l.tag.toLowerCase().split('-')[0] === lang);
    if (byLang) return byLang.id;
  }
  return DEFAULT_LOCALE;
}

/**
 * CLDR plural categories. A catalogue node whose every child is one of these is a plural set,
 * not five more keys — which is how the key-parity test can demand identical key sets while
 * Polish carries `few`/`many` and English carries only `one`/`other`.
 */
export const PLURAL_CATEGORIES: readonly string[] = ['zero', 'one', 'two', 'few', 'many', 'other'];

export function isPluralNode(node: unknown): node is Record<string, string> {
  if (node === null || typeof node !== 'object' || Array.isArray(node)) return false;
  const keys = Object.keys(node);
  return keys.length > 0 && keys.every((k) => PLURAL_CATEGORIES.includes(k));
}
